import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'localization';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { ChannelScope } from 'types/channel';
import { UseCaseRow } from 'types/schema';
import type { Publisher } from 'types/publisher';
import { createChannel, CreateChannelData } from '../../../api/marketplaceApi';
import UseCaseTagPickerMolecule from '../../molecules/marketplace/UseCaseTagPickerMolecule';
import SphereSearch, { SphereOption } from '../../molecules/SphereSearch/SphereSearch';
import PublisherSelectMolecule from '../../molecules/marketplace/PublisherSelectMolecule';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import PublisherCreateDialogOrganism from './PublisherCreateDialogOrganism';

export interface CreateChannelDialogOrganismProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  useCases: UseCaseRow[];
  useCasesLoading: boolean;
  useCasesError: string | null;
}

const SCOPE_VALUES: ChannelScope[] = ['center', 'organization', 'metasphere'];

const SCOPE_ICON: Record<ChannelScope, string> = {
  center: 'pi-bullseye',
  organization: 'pi-building',
  metasphere: 'pi-globe',
};

const CreateChannelDialogOrganism: React.FC<CreateChannelDialogOrganismProps> = ({
  open,
  onClose,
  onCreated,
  useCases,
  useCasesLoading,
  useCasesError,
}) => {
  const { t } = useTranslation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;
  const orgName = (impersonationDetails as { organizationName?: string } | null)?.organizationName;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [scope, setScope] = useState<ChannelScope>('center');
  const [selectedCenter, setSelectedCenter] = useState<SphereOption | null>(null);
  const [selectedPublisherSystemName, setSelectedPublisherSystemName] = useState<string | null>(null);
  const [selectedPublisherDisplayName, setSelectedPublisherDisplayName] = useState<string | null>(null);
  const [showCreatePublisherDialog, setShowCreatePublisherDialog] = useState(false);
  const [useCaseTags, setUseCaseTags] = useState<string[]>([]);
  const [glyph, setGlyph] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setScope('center');
    setSelectedCenter(null);
    setSelectedPublisherSystemName(null);
    setSelectedPublisherDisplayName(null);
    setShowCreatePublisherDialog(false);
    setUseCaseTags([]);
    setGlyph(null);
    setError(null);
    setSubmitting(false);
    setSubmitAttempted(false);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleScopeChange = useCallback((nextScope: ChannelScope) => {
    setScope(nextScope);
    setSubmitAttempted(false);
    if (nextScope !== 'center') {
      setSelectedCenter(null);
    }
    if (nextScope !== 'metasphere') {
      setSelectedPublisherSystemName(null);
      setSelectedPublisherDisplayName(null);
    }
  }, []);

  const centerId = selectedCenter?.code || '';

  const scopeId = useMemo(() => {
    if (scope === 'center') return centerId;
    if (scope === 'organization') return organizationId || '';
    return '00000000-0000-0000-0000-000000000000';
  }, [scope, centerId, organizationId]);

  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (scope === 'center' && !centerId) return false;
    if (scope === 'organization' && !organizationId) return false;
    if (scope === 'metasphere' && !selectedPublisherSystemName) return false;
    return true;
  }, [name, scope, centerId, organizationId, selectedPublisherSystemName]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      setSubmitAttempted(true);

      if (!canSubmit || !apiClients || !organizationId) return;

      setSubmitting(true);
      setError(null);

      const data: CreateChannelData = {
        name: { default: name.trim() },
        scope,
        scopeId,
      };

      if (description.trim()) {
        data.description = { default: description.trim() };
      }

      if (useCaseTags.length > 0) {
        data.useCaseTags = useCaseTags;
      }

      if (glyph) {
        data.glyph = glyph;
      }

      if (scope === 'metasphere' && selectedPublisherSystemName) {
        data.publisherSystemName = selectedPublisherSystemName;
      }

      try {
        await createChannel(apiClients.schemaClient, data);
        resetForm();
        onCreated();
      } catch (err) {
        if (err && typeof err === 'object' && 'response' in err) {
          const axiosErr = err as { response?: { status?: number } };
          if (axiosErr.response?.status === 409) {
            setError(t('marketplace.channels.create.errorConflict'));
          } else {
            setError(t('marketplace.channels.create.errorGeneric'));
          }
        } else {
          setError(t('marketplace.channels.create.errorFallback'));
        }
      } finally {
        setSubmitting(false);
      }
    },
    [canSubmit, apiClients, organizationId, name, description, scope, scopeId, useCaseTags, glyph, selectedPublisherSystemName, resetForm, onCreated, t]
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        handleClose();
      }
    },
    [handleClose]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    },
    [handleClose]
  );

  const handlePublisherCreated = useCallback((publisher: Publisher) => {
    setSelectedPublisherSystemName(publisher.system_name);
    setSelectedPublisherDisplayName(publisher.display_name);
    setShowCreatePublisherDialog(false);
  }, []);

  if (!open) return null;

  return (
    <>
      <div
        className="marketplace-dialog-overlay"
        onClick={handleOverlayClick}
        onKeyDown={handleKeyDown}
      >
        <div
          className="marketplace-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-channel-title"
        >
          <div className="marketplace-dialog__header">
            <div>
              <h2 id="create-channel-title" className="marketplace-dialog__title">
                {t('marketplace.channels.create.title')}
              </h2>
              <p className="marketplace-dialog__subtitle">
                {t('marketplace.channels.create.subtitle')}
              </p>
            </div>
            <ButtonAtom
              variant="text"
              shape="icon-square"
              icon="pi pi-times"
              className="marketplace-dialog__close-btn"
              aria-label={t('marketplace.channels.create.closeLabel')}
              onClick={handleClose}
            />
          </div>

          <form className="marketplace-dialog__body" onSubmit={handleSubmit}>
            {/* Channel Name */}
            <div className="marketplace-dialog__field">
              <label className="marketplace-dialog__label" htmlFor="channel-name">
                {t('marketplace.channels.create.nameLabel')} <span className="marketplace-dialog__required">*</span>
              </label>
              <input
                id="channel-name"
                type="text"
                className="marketplace-dialog__input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('marketplace.channels.create.namePlaceholder')}
                required
                disabled={submitting}
              />
            </div>

            {/* Description */}
            <div className="marketplace-dialog__field">
              <label className="marketplace-dialog__label" htmlFor="channel-description">
                {t('marketplace.channels.create.descriptionLabel')}
              </label>
              <textarea
                id="channel-description"
                className="marketplace-dialog__textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('marketplace.channels.create.descriptionPlaceholder')}
                rows={3}
                disabled={submitting}
              />
            </div>

            {/* Scope */}
            <fieldset className="marketplace-dialog__scope-group">
              <legend className="marketplace-dialog__label marketplace-dialog__scope-group-legend">
                {t('marketplace.channels.create.scopeLabel')} <span className="marketplace-dialog__required">*</span>
              </legend>
              {SCOPE_VALUES.map((scopeValue) => (
                <label
                  key={scopeValue}
                  className={`marketplace-dialog__scope-card marketplace-dialog__scope-card--${scopeValue}${scope === scopeValue ? ' marketplace-dialog__scope-card--active' : ''}`}
                >
                  <input
                    type="radio"
                    name="scope"
                    value={scopeValue}
                    checked={scope === scopeValue}
                    onChange={() => handleScopeChange(scopeValue)}
                    disabled={submitting}
                    className="marketplace-dialog__scope-card-radio"
                  />
                  <div className="marketplace-dialog__scope-card-body">
                    <div className="marketplace-dialog__scope-card-title-row">
                      <i className={`pi ${SCOPE_ICON[scopeValue]}`} aria-hidden="true" />
                      <span className="marketplace-dialog__scope-card-name">
                        {t(`marketplace.common.scope.${scopeValue}`)}
                      </span>
                      {scopeValue === 'metasphere' && (
                        <span className="marketplace-dialog__scope-card-pill">
                          {t('marketplace.channels.create.reviewRequired')}
                        </span>
                      )}
                    </div>
                    <div className="marketplace-dialog__scope-card-desc">
                      {t(`marketplace.channels.create.scopeDescription.${scopeValue}`)}
                    </div>
                  </div>
                </label>
              ))}
            </fieldset>

            {/* Per-scope info panel */}
            {scope === 'center' && (
              <div className="marketplace-dialog__field">
                <label className="marketplace-dialog__label" htmlFor="channel-center-search">
                  {t('marketplace.channels.create.centerLabel')} <span className="marketplace-dialog__required">*</span>
                </label>
                <SphereSearch
                  single
                  organizationId={organizationId ?? ''}
                  filters={[{ property: 'label', operator: 'equals', value: 'center' }] as any}
                  filterOperator="and"
                  placeholder={t('marketplace.channels.create.centerPlaceholder')}
                  initialSelectedSpheres={selectedCenter ? [selectedCenter] : []}
                  onSphereSelected={(_all, single) => setSelectedCenter(single ?? null)}
                />
                {submitAttempted && selectedCenter === null && (
                  <div className="marketplace-dialog__no-centers-warning" role="alert">
                    {t('marketplace.channels.create.noCentersWarning')}
                  </div>
                )}
              </div>
            )}

            {scope === 'organization' && (
              <div className="marketplace-dialog__scope-info marketplace-dialog__scope-info--organization">
                <i className="pi pi-building" aria-hidden="true" />
                <span>
                  {orgName
                    ? t('marketplace.channels.create.orgInfo', { orgName })
                    : t('marketplace.channels.create.orgInfoFallback')}
                </span>
              </div>
            )}

            {scope === 'metasphere' && (
              <>
                <div className="marketplace-dialog__scope-info marketplace-dialog__scope-info--metasphere">
                  <i className="pi pi-info-circle" aria-hidden="true" />
                  <span>{t('marketplace.channels.create.metasphereNotice')}</span>
                </div>
                <PublisherSelectMolecule
                  value={selectedPublisherSystemName}
                  displayName={selectedPublisherDisplayName}
                  onChange={(systemName, displayName) => {
                    setSelectedPublisherSystemName(systemName);
                    setSelectedPublisherDisplayName(displayName);
                  }}
                  onCreateNew={() => setShowCreatePublisherDialog(true)}
                  disabled={submitting}
                  submitAttempted={submitAttempted}
                />
              </>
            )}

            {/* Glyph Picker — hidden in new design */}

            {/* Use Case Tags */}
            <UseCaseTagPickerMolecule
              selectedTags={useCaseTags}
              onChange={setUseCaseTags}
              useCases={useCases}
              useCasesLoading={useCasesLoading}
              useCasesError={useCasesError}
            />

            {/* Error display */}
            {error && (
              <div className="marketplace-dialog__error">{error}</div>
            )}

            {/* Actions */}
            <div className="marketplace-dialog__footer marketplace-dialog__actions">
              <ButtonAtom
                variant="secondary"
                shape="pill"
                type="button"
                label={t('marketplace.channels.create.cancelButton')}
                onClick={handleClose}
                disabled={submitting}
              />
              <ButtonAtom
                variant="primary"
                shape="pill"
                type="submit"
                icon={submitting ? 'pi pi-spinner pi-spin' : 'pi pi-plus'}
                iconPos="left"
                label={(() => {
                  if (submitting) return t('marketplace.channels.create.creating');
                  if (scope === 'metasphere') return t('marketplace.channels.create.submitForReview');
                  return t('marketplace.channels.create.createButton');
                })()}
                disabled={!canSubmit || submitting}
              />
            </div>
          </form>
        </div>
      </div>

      <PublisherCreateDialogOrganism
        open={showCreatePublisherDialog}
        onClose={() => setShowCreatePublisherDialog(false)}
        onCreated={handlePublisherCreated}
      />
    </>
  );
};

export { CreateChannelDialogOrganism };
export default CreateChannelDialogOrganism;
